/**
 * Il QR, disegnato senza canvas e senza immagini.
 *
 * Un quadrato per modulo, dentro un contenitore a griglia. Niente `<canvas>` — su Tizen il contesto
 * 2D esiste ma la resa dipende dal modello, e un QR sfocato non si legge — e niente immagini da
 * scaricare, perché questa schermata si vede **prima** di avere un gateway a cui chiederle.
 *
 * La codifica la fa `qrcode-generator`: ES5, nessuna funzione a freccia, nessuna classe, e nessuno
 * dei metodi che Tizen 5 non ha. Scriverla a mano sarebbe stato duecento righe di Reed-Solomon che
 * sembrano giuste e che si scoprono sbagliate sul telefono di un rivenditore, dove nessuno le può
 * correggere.
 */

import type { ReactNode } from 'react';
import qrcode from 'qrcode-generator';

export interface QrCodeProps {
  /** Cosa ci va dentro. Per l'accoppiamento è **il codice e basta** — vedi SPEC §4.1. */
  readonly text: string;
  /** Lato del quadrato, in pixel di disegno. */
  readonly sizePx: number;
  readonly dark?: string;
  readonly light?: string;
}

/**
 * Il margine obbligatorio, in moduli.
 *
 * Quattro, come dice la norma. Non è decorazione: senza, un lettore non distingue dove finisce il
 * codice e comincia lo sfondo, e su uno schermo scuro il QR diventa semplicemente illeggibile.
 */
const QUIET_ZONE = 4;

export function QrCode({ text, sizePx, dark = '#000000', light = '#FFFFFF' }: QrCodeProps): ReactNode {
  // Tipo 0 = lo sceglie da sé in base a quanto testo c'è; `M` è il compromesso abituale fra
  // densità e tolleranza allo sporco — e uno schermo acceso ripreso di sbieco è sporco.
  const qr = qrcode(0, 'M');
  qr.addData(text);
  qr.make();

  const modules = qr.getModuleCount();
  const total = modules + QUIET_ZONE * 2;
  const cell = sizePx / total;

  const cells: ReactNode[] = [];
  for (let row = 0; row < modules; row += 1) {
    for (let column = 0; column < modules; column += 1) {
      if (!qr.isDark(row, column)) continue;
      cells.push(
        <span
          key={`${row}:${column}`}
          style={{
            position: 'absolute',
            left: (column + QUIET_ZONE) * cell,
            top: (row + QUIET_ZONE) * cell,
            // Un pelo di sovrapposizione: a dimensioni non intere i bordi lasciano righe chiare
            // fra un modulo e l'altro, e un lettore le legge come moduli spenti.
            width: cell + 0.5,
            height: cell + 0.5,
            background: dark,
          }}
        />,
      );
    }
  }

  return (
    <div
      data-testid="qr-code"
      aria-hidden
      style={{
        position: 'relative',
        width: sizePx,
        height: sizePx,
        background: light,
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      {cells}
    </div>
  );
}
